import { useState, useEffect, useRef, useMemo } from 'react';
import { CarData } from '../components/CarInfo/CarInfo';
import { useConsecutiveEvents } from './useConsecutiveEvents';
import { getConfig } from '../constants';

interface UseCarSortationSSEReturn {
  currentCar: CarData | null;
  isConnected: boolean;
  error: string | null;
}

export const useCarSortationSSE = (url?: string): UseCarSortationSSEReturn => {
  const defaultUrl = url || '';
  const [currentCar, setCurrentCar] = useState<CarData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const lastMessageTimeRef = useRef<number>(0);

  const threshold = useMemo(() => {
    return getConfig().CONSECUTIVE_EVENTS_THRESHOLD;
  }, []);

  const { processEvent } = useConsecutiveEvents({
    threshold,
  });

  useEffect(() => {
    // Don't run if URL is empty
    if (!defaultUrl) {
      console.log('SSE hook: No URL provided, skipping connection');
      return;
    }
    let eventSource: EventSource | null = null;
    let connectionTimeout: NodeJS.Timeout | null = null;

    const connectToSSE = () => {
      try {
        console.log('Connecting to SSE:', defaultUrl);
        eventSource = new EventSource(defaultUrl);

        // Set a timeout to detect if connection fails
        connectionTimeout = setTimeout(() => {
          if (!isConnected) {
            console.log('SSE connection timeout');
            eventSource?.close();
            setError('Connection timeout');
          }
        }, 5000);

        eventSource.onopen = () => {
          console.log('SSE connected');
          setIsConnected(true);
          setError(null);
          retryCountRef.current = 0;
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
          }
        };

        eventSource.onmessage = (event) => {
          try {
            const now = Date.now();

            // Calculate time since last message
            if (lastMessageTimeRef.current > 0) {
              const timeDiff = now - lastMessageTimeRef.current;
              console.log(`SSE Message (${timeDiff}ms):`, event.data);
            } else {
              console.log('SSE Message (first):', event.data);
            }
            lastMessageTimeRef.current = now;

            const rawData = JSON.parse(event.data);

            processEvent(rawData, setCurrentCar);
          } catch (error) {
            console.error('Error parsing car data:', error);
            setError('Failed to parse car data');
          }
        };

        eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          setIsConnected(false);
          setError('Connection error');

          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            console.log(`Retry ${retryCountRef.current}/${maxRetries}`);
            setTimeout(() => {
              eventSource?.close();
              connectToSSE();
            }, 2000 * retryCountRef.current); // Exponential backoff
          } else {
            console.log('SSE connection failed');
            setError('Connection failed after retries');
          }
        };
      } catch (error) {
        console.error('Failed to create SSE connection:', error);
        setError('Failed to create connection');
      }
    };

    // Try to connect to SSE first
    connectToSSE();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }
      setIsConnected(false);
    };
  }, [defaultUrl]);

  return {
    currentCar,
    isConnected,
    error,
  };
};
